'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Menu, X, ChevronDown } from 'lucide-react';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTop10Open, setIsTop10Open] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link href="/" className="flex-shrink-0 group" aria-label="Spire Group - Luxury Real Estate">
            <div className="flex items-center gap-2 md:gap-3">
              <Image
                src="/spire-logo-black.webp"
                alt="Spire Group"
                width={160}
                height={50}
                className="h-10 md:h-14 w-auto"
                priority
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex gap-1 lg:gap-2" aria-label="Main navigation">
            <Link
              href="/properties"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-[#d60500] hover:bg-[#d60500]/10 rounded-lg transition-all"
              aria-label="Browse properties"
            >
              Properties
            </Link>
            <Link
              href="/properties?listing_type=For Sale"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-[#d60500] hover:bg-[#d60500]/10 rounded-lg transition-all"
              aria-label="Properties For Sale"
            >
              For Sale
            </Link>
            <Link
              href="/properties?listing_type=For Rent"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-[#d60500] hover:bg-[#d60500]/10 rounded-lg transition-all"
              aria-label="Properties For Rent"
            >
              For Rent
            </Link>
            <Link
              href="/neighborhoods"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-[#d60500] hover:bg-[#d60500]/10 rounded-lg transition-all"
              aria-label="Explore neighborhoods"
            >
              Neighborhoods
            </Link>

            {/* Top 10 Dropdown */}
            <div className="relative" onMouseEnter={() => setIsTop10Open(true)} onMouseLeave={() => setIsTop10Open(false)}>
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-[#d60500] hover:bg-[#d60500]/10 rounded-lg transition-all flex items-center gap-1"
                aria-label="Top 10 Lists"
              >
                Top 10
                <ChevronDown className={`w-4 h-4 transition-transform ${isTop10Open ? 'rotate-180' : ''}`} />
              </button>
              {isTop10Open && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <Link href="/top-10-houses" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500]">
                    Most Expensive Houses
                  </Link>
                  <Link href="/top-10-condos" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500]">
                    Most Expensive Condos
                  </Link>
                  <div className="border-t border-gray-200 my-2"></div>
                  <Link href="/top-10-houses-for-rent" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500]">
                    Houses for Rent
                  </Link>
                  <Link href="/top-10-condos-for-rent" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500]">
                    Condos for Rent
                  </Link>
                  <div className="border-t border-gray-200 my-2"></div>
                  <Link href="/top-10-properties-makati" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500]">
                    Makati City Properties
                  </Link>
                </div>
              )}
            </div>

            <Link
              href="/about"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-[#d60500] hover:bg-[#d60500]/10 rounded-lg transition-all"
              aria-label="About Spire Group"
            >
              About Us
            </Link>
            <Link
              href="/blogs"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-[#d60500] hover:bg-[#d60500]/10 rounded-lg transition-all"
              aria-label="Blog articles and insights"
            >
              Blog
            </Link>
            <Link
              href="/contact"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-[#d60500] hover:bg-[#d60500]/10 rounded-lg transition-all"
              aria-label="Contact us"
            >
              Contact
            </Link>
          </nav>

          {/* Desktop CTA Button */}
          <div className="hidden md:flex items-center gap-3">
            {/* List Your Property Button */}
            <Link
              href="/list-property"
              className="px-4 py-2.5 bg-[#d60500] hover:bg-[#b50400] text-white rounded-lg text-sm font-semibold transition-all shadow-sm hover:shadow-md"
              aria-label="List your property with us"
            >
              List Your Property
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <nav className="md:hidden border-t border-gray-200 py-4 space-y-1 animate-in fade-in slide-in-from-top-2 duration-200" aria-label="Mobile navigation">
            <Link
              href="/properties"
              className="block px-4 py-3 text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] rounded-lg transition-all font-medium"
              aria-label="Browse properties"
              onClick={() => setIsMenuOpen(false)}
            >
              Properties
            </Link>
            <Link
              href="/properties?listing_type=For Sale"
              className="block px-4 py-3 text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] rounded-lg transition-all font-medium"
              aria-label="Properties For Sale"
              onClick={() => setIsMenuOpen(false)}
            >
              For Sale
            </Link>
            <Link
              href="/properties?listing_type=For Rent"
              className="block px-4 py-3 text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] rounded-lg transition-all font-medium"
              aria-label="Properties For Rent"
              onClick={() => setIsMenuOpen(false)}
            >
              For Rent
            </Link>
            <Link
              href="/neighborhoods"
              className="block px-4 py-3 text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] rounded-lg transition-all font-medium"
              aria-label="Explore neighborhoods"
              onClick={() => setIsMenuOpen(false)}
            >
              Neighborhoods
            </Link>

            {/* Top 10 Links - Mobile */}
            <div className="border-t border-gray-200 my-2 pt-2">
              <div className="px-4 pb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Top 10 Lists
              </div>
              <Link
                href="/top-10-houses"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] rounded-lg transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                Most Expensive Houses
              </Link>
              <Link
                href="/top-10-condos"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] rounded-lg transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                Most Expensive Condos
              </Link>
              <Link
                href="/top-10-houses-for-rent"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] rounded-lg transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                Houses for Rent
              </Link>
              <Link
                href="/top-10-condos-for-rent"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] rounded-lg transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                Condos for Rent
              </Link>
              <Link
                href="/top-10-properties-makati"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] rounded-lg transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                Makati City Properties
              </Link>
            </div>

            <Link
              href="/about"
              className="block px-4 py-3 text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] rounded-lg transition-all font-medium"
              aria-label="About Spire Group"
              onClick={() => setIsMenuOpen(false)}
            >
              About Us
            </Link>
            <Link
              href="/blogs"
              className="block px-4 py-3 text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] rounded-lg transition-all font-medium"
              aria-label="Blog articles and insights"
              onClick={() => setIsMenuOpen(false)}
            >
              Blog
            </Link>
            <Link
              href="/contact"
              className="block px-4 py-3 text-gray-700 hover:bg-[#d60500]/10 hover:text-[#d60500] rounded-lg transition-all font-medium"
              aria-label="Contact us"
              onClick={() => setIsMenuOpen(false)}
            >
              Contact
            </Link>

            {/* Mobile CTA Button */}
            <div className="px-4 pt-4 border-t border-gray-200 mt-4">
              <Link
                href="/list-property"
                className="block w-full px-4 py-3 bg-[#d60500] hover:bg-[#b50400] text-white rounded-lg text-sm font-semibold transition-all shadow-sm hover:shadow-md text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                List Your Property
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
